import { Pie } from 'react-chartjs-2';

import { Chart, plugins, elements } from 'chart.js';
import { buildChartOptions } from 'structure/capital-manager/utils/charts';
import { ChartLegend } from 'structure/capital-manager/components/ChartLedend';
import { TokenTableRow } from 'structure/capital-manager/types';
import { useMemo } from 'react';

// Register necessary components
Chart.register(
  elements.ArcElement,
  elements.LineElement,
  elements.PointElement,
  elements.BarElement,
  plugins.Title,
  plugins.Tooltip,
  plugins.Legend,
  plugins.SubTitle
);

const colorMap = {
  stable: 'rgb(94,181,57)',
  bluechip: 'rgb(0,115,193)',
};

const labelMap = {
  bluechip: 'Bluechip',
  altcoin: 'Altcoins',
  stable: 'Stables',
};

const getColorForAssetType = (assetType: string) => {
  return (
    (colorMap as Record<string, string>)[assetType.toLowerCase()] ||
    'rgb(191,75,97)'
  ); // default to a color if unknown
};

interface AssetsRiskChartProps {
  tokens: TokenTableRow[];
}

export const ChartAssetRisk = (props: AssetsRiskChartProps) => {
  const { tokens = [] } = props;

  const groupByAssetType = useMemo(() => {
    return tokens.reduce(
      (acc, token) => {
        const assetType = token.tokenGroup;
        if (!acc[assetType]) {
          acc[assetType] = token.balanceUsd;
        } else {
          acc[assetType] += token.balanceUsd;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [tokens]);

  const sortedEntries = Object.entries(groupByAssetType)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  const labels = sortedEntries.map((item) => item.label);
  const values = sortedEntries.map((item) => item.value);

  const readableLabels = labels.map(
    (label) => (labelMap as Record<string, string>)[label] || label
  );

  const data = {
    labels: readableLabels,
    datasets: [
      {
        label: 'Percentage',
        data: values.map((item) => Number(item.toFixed(2))),
        backgroundColor: labels.map((label) => getColorForAssetType(label)),
        hoverOffset: 15,
        borderWidth: 0,
      },
    ],
  };

  const options = buildChartOptions({
    title: 'Asset risk distribution',
  });

  return (
    <div className='flex flex-col items-center gap-4'>
      <Pie options={options} data={data} />
      <ChartLegend labels={readableLabels} dataset={data.datasets[0]} />
    </div>
  );
};
